<template>
  <div class="dashboard">
    <!-- Sidebar -->
    <div :class="['sidebar', { 'sidebar-collapsed': !isSidebarExpanded }]">
      <Sidebar />
    </div>

    <!-- Main Content -->
    <div
      :class="['main-content', { 'main-content-expanded': isSidebarExpanded }]"
    >
      <!-- Header -->
      <header>
        <div class="header-left">
          <i class="fas fa-bars" @click="handleSidebarToggle"></i>
          <WaitingDriversNumber :waiting-captains="waitingCaptains" />
        </div>
        <div class="header-right">
          <i class="fas fa-plus-circle"></i>
        </div>
      </header>

      <!-- Users List -->
      <div class="users-list">
        <div class="users-header">
          <h2>Users list</h2>
          <div class="search-bar">
            <input
              type="text"
              placeholder="Search by Name, Phone or ID"
              v-model.trim="searchQuery"
              @input="onSearchInput"
            />
            <i class="fas fa-search"></i>
          </div>
        </div>

        <!-- Loading, Error, and Table States -->
        <div v-if="isLoading" class="loading-state">
          <p>Loading users...</p>
        </div>
        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
        </div>
        <div v-else>
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Phone Number</th>
                  <th>Completed Trips</th>
                  <th>Cancelled Trips</th>
                  <th>Wallet</th>
                  <th>Rate</th>
                  <th>Actions</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="user in users"
                  :key="user._id"
                  @click="goToUser(user.id)"
                  style="cursor: pointer"
                >
                  <td>
                    <div class="user-info">
                      <img
                        :src="user.image || 'https://via.placeholder.com/40'"
                        alt="User"
                        class="user-image"
                      />
                      <div>
                        <p class="user-name">{{ user.name || "N/A" }}</p>
                        <p class="user-phone">
                          {{ user.phoneNumber || "N/A" }}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>{{ user.phoneNumber || "N/A" }}</td>
                  <td>{{ user.completedTrips }}</td>
                  <td>{{ user.cancelledTrips || 0 }}</td>
                  <td>{{ user.wallet || "0 EGP" }}</td>
                  <td>
                    <span class="stars">★ {{ user.rating || "0.0" }}</span>
                  </td>
                  <td>
                    <i
                      class="fas fa-trash-alt delete-icon"
                      @click.stop="deleteUser(user._id)"
                    ></i>
                  </td>
                  <td>View Details</td>
                </tr>

                <tr v-if="!isLoading && users.length === 0">
                  <td colspan="8" class="no-data">No users found</td>
                </tr>
              </tbody>
            </table>

            <!-- Footer with Pagination -->
            <!-- Footer with Pagination -->

            <div class="users-footer">
              <p class="users-total-note">
                {{ pageStart }}-{{ pageEnd }} of {{ totalItems }} items
              </p>

              <div class="pager">
                <button
                  class="icon-btn"
                  :disabled="currentPage <= 1"
                  @click="goPrev"
                  aria-label="Previous page"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>

                <div class="page-pill">{{ currentPage }}</div>

                <button
                  class="icon-btn icon-btn--primary"
                  :disabled="currentPage >= totalPages"
                  @click="goNext"
                  aria-label="Next page"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import Sidebar from "./sidebarComponent.vue";
import WaitingDriversNumber from "@/components/waitingDriversNumber.vue";

export default {
  name: "UsersListComponent",
  components: { WaitingDriversNumber, Sidebar },
  data() {
    return {
      isSidebarExpanded: true,
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 10, // used as "limit" for the API
      users: [],
      totalItems: 0, // total count from server
      isLoading: false,
      error: null,
      adminUsername: localStorage.getItem("username"),
      debounceHandle: null,
    };
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
    pageStart() {
      return this.totalItems === 0
        ? 0
        : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },
  },
  watch: {
    currentPage() {
      if (this.debounceHandle) {
        clearTimeout(this.debounceHandle);
        this.debounceHandle = null;
      }
      this.loadUsers();
    },
    itemsPerPage() {
      if (this.debounceHandle) {
        clearTimeout(this.debounceHandle);
        this.debounceHandle = null;
      }
      this.currentPage = 1;
      this.loadUsers();
    },
  },
  created() {
    this.loadUsers();
  },
  beforeUnmount() {
    if (this.debounceHandle) clearTimeout(this.debounceHandle);
  },
  methods: {
    handleSidebarToggle() {
      this.isSidebarExpanded = !this.isSidebarExpanded;
    },
    goToUser(id) {
      if (!id) {
        console.error("Invalid user ID:", id);
        alert("Cannot navigate: Invalid user ID");
        return;
      }
      this.$router.push(`/user/${id}`);
    },

    onSearchInput() {
      // debounce to avoid hammering the API while typing
      clearTimeout(this.debounceHandle);
      this.debounceHandle = setTimeout(() => {
        this.currentPage = 1;
        this.loadUsers();
      }, 350);
    },
    goPrev() {
      if (this.currentPage > 1) this.currentPage--;
    },
    goNext() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },

    async loadUsers() {
      this.isLoading = true;
      this.error = null;

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // نستخدم searchUsers دايمًا — حتى لو مفيش كلمة بحث
        const payload = {
          value:
            this.searchQuery && this.searchQuery.trim()
              ? this.searchQuery.trim()
              : "",
          page: this.currentPage,
          limit: this.itemsPerPage,
        };

        const res = await axios.post(
          "https://backend.fego-rides.com/admin/searchUsers",
          payload,
          { headers }
        );

        const d = res && res.data ? res.data : {};
        const list = Array.isArray(d.data)
          ? d.data
          : Array.isArray(d.users)
          ? d.users
          : [];

        this.users = list.map((u) => ({
          _id: u._id ?? u.id ?? null,
          id: u.id ?? u._id ?? null,
          name:
            u.username ||
            `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
            "N/A",
          phoneNumber: u.phoneNumber || u.mobile || "N/A",
          image: u.profile_image || "https://via.placeholder.com/40",
          completedTrips: u.endCount || u.completedTrips || 0,
          cancelledTrips: u.cancelledCount || 0,
          wallet:
            typeof u.wallet === "number"
              ? `${u.wallet} EGP`
              : u.wallet || "0 EGP",
          rating: u.rate ?? u.rating ?? "0.0",
        }));

        // إجمالي العناصر من الاستجابة
        let total = d.total;
        if (total == null) total = d.totalUsers;
        if (total == null) total = d.count;
        if (total == null) total = d.totalCount;
        if (total == null && d.pagination) total = d.pagination.total;
        if (total == null && d.meta) total = d.meta.total;
        if (total == null) {
          // fallback لو السيرفر ما بيرجعش total (نادرًا)
          total =
            (this.currentPage - 1) * this.itemsPerPage +
            (Array.isArray(list) ? list.length : 0);
        }
        this.totalItems = Number(total);
      } catch (e) {
        console.error(e);
        this.error = "Failed to load users. Please try again later.";
        this.users = [];
        this.totalItems = 0;
      } finally {
        this.isLoading = false;
      }
    },

    async searchUsers() {
      this.isLoading = true;
      this.error = null;

      try {
        const payload = {
          value: this.searchQuery || "", // can be name/phone/_id
          page: this.currentPage,
          limit: this.itemsPerPage,
        };

        const headers = {};
        const token = localStorage.getItem("token");
        if (token) headers.Authorization = `Bearer ${token}`;

        const { data: res } = await axios.post(
          "https://backend.fego-rides.com/admin/UsersPanel",
          payload,
          { headers }
        );

        // Flexible parsing in case the backend shape differs
        const list = res?.data || res?.users || [];
        const total =
          res?.total ||
          res?.count ||
          res?.totalDocs ||
          res?.length ||
          (Array.isArray(list) ? list.length : 0);

        this.users = list.map((user) => ({
          _id: user._id ?? user.id ?? null,
          id: user.id ?? user._id ?? null,
          name:
            user.username ||
            `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
            "N/A",
          phoneNumber: user.phoneNumber || user.mobile || "N/A",
          image: user.profile_image || "https://via.placeholder.com/40",
          completedTrips: user.endCount || user.completedTrips || 0,
          cancelledTrips: user.cancelledCount || 0,
          wallet:
            typeof user.wallet === "number"
              ? `${user.wallet} EGP`
              : user.wallet || "0 EGP",
          rating: user.rate ?? user.rating ?? "0.0",
        }));

        this.totalItems = Number.isFinite(total) ? total : this.users.length;
      } catch (err) {
        console.error("Error searching users:", err);
        this.error = "Failed to load users. Please try again later.";
        this.users = [];
        this.totalItems = 0;
      } finally {
        this.isLoading = false;
      }
    },

    async deleteUser(userId) {
      if (!confirm("Are you sure you want to delete this user?")) return;
      try {
        const headers = {};
        const token = localStorage.getItem("token");
        if (token) headers.Authorization = `Bearer ${token}`;

        await axios.delete(
          `https://backend.fego-rides.com/admin/delete-user/${userId}`,
          { headers }
        );
        this.users = this.users.filter((user) => user._id !== userId);
        this.totalItems = Math.max(0, this.totalItems - 1);
        alert("User deleted successfully");
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user. Please try again.");
      }
    },
  },
};
</script>

<style scoped>
/* Base Styles */
.dashboard {
  display: flex;
  min-height: 100vh;
  font-family: "Arial", sans-serif;
}

/* Sidebar */
.sidebar {
  width: 250px;
  transition: width 0.3s ease;
  color: #ffffff;
}

.sidebar-collapsed {
  width: 80px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  padding: 16px;
}

.sidebar-header .logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 8px;
}

.sidebar-header span {
  font-size: 18px;
  font-weight: 600;
}

.sidebar-menu {
  margin-top: 16px;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
}

.menu-item i {
  margin-right: 12px;
  font-size: 18px;
}

.menu-item span {
  font-size: 16px;
}

.menu-item.active {
  background-color: #ffffff;
  color: #6b48ff;
  border-left: 4px solid #6b48ff;
}

/* Main Content */
.main-content {
  flex: 1;
  background-color: #f5f7fa;
  padding: 24px;
  transition: margin-left 0.3s ease;
}

.main-content-expanded.sidebar-collapsed {
  margin-left: 80px;
}

/* Header */
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-left i {
  font-size: 20px;
  color: #6b7280;
  margin-right: 16px;
  cursor: pointer;
}

.header-left h1 {
  font-size: 24px;
  font-weight: 600;
  color: #1f2a44;
  margin: 0;
}

.header-left p {
  font-size: 14px;
  color: #2563eb;
  cursor: pointer;
  margin: 4px 0 0 0;
}

.header-right i {
  font-size: 20px;
  color: #6b7280;
}

/* Users List */
.users-list {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.users-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.users-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2a44;
  margin: 0;
}

.search-bar {
  position: relative;
  width: 200px;
}

.search-bar input {
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 14px;
  color: #374151;
}

.search-bar i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #6b7280;
}

/* Table */
.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 12px;
  text-align: left;
}

th {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  background-color: #f9fafb;
  text-transform: uppercase;
}

td {
  font-size: 14px;
  color: #374151;
  border-top: 1px solid #e5e7eb;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2a44;
  margin: 0;
}

.user-phone {
  font-size: 12px;
  color: #6b7280;
  margin: 2px 0 0 0;
}

.stars {
  font-size: 12px;
  color: #f59e0b;
}

.delete-icon {
  font-size: 16px;
  color: #ff4d4f;
  cursor: pointer;
}

.delete-icon:hover {
  color: #e63946;
}

.action-open {
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
}

.action-open.disabled {
  color: #6b7280;
  pointer-events: none;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #888;
}

/* Loading and Error States */
.loading-state,
.error-state {
  text-align: center;
  padding: 20px;
}

.error-message {
  color: #ff4d4f;
}

.users-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.users-total-note {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.pager {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #e5e7eb; /* رمادي للسهم اليسار (وحيتغير لما يكون Primary) */
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-btn:disabled {
  opacity: 0.9;
  cursor: not-allowed;
}

.icon-btn--primary {
  background: #6b48ff; /* بنفسجي */
  border-color: #6b48ff;
  color: #fff; /* السهم أبيض */
}

.page-pill {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  background: #6b48ff;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
}
.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-indicator {
  min-width: 70px;
  text-align: center;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  font-weight: 600;
}

.users-total-note {
  margin-top: 8px;
  font-size: 14px;
  color: #6b7280;
  text-align: right;
}

.pagination p {
  font-size: 14px;
  color: #6b7280;
}

.pagination-buttons {
  display: flex;
  gap: 8px;
}

.pagination-buttons button {
  padding: 4px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: #ffffff;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
}

.pagination-buttons button.active {
  background-color: #2563eb;
  color: #ffffff;
  border-color: #2563eb;
}

.pagination-buttons button:disabled {
  color: #d1d5db;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 16px;
  }

  .sidebar {
    width: 80px;
  }

  .main-content-expanded {
    margin-left: 80px;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    margin-top: 8px;
  }

  .users-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .search-bar {
    width: 100%;
  }

  .users-footer {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
